import { useInViewport } from "ahooks"
import { useEffect, useRef } from "react"
import { useEventCallback } from "src/components/hooks/useEventCallback"
import { useWindowLocationPathname } from "src/components/hooks/useWindowLocationPathname"

export type FunctionRef = (node?: Element | null) => void

// This must be a shared object and not a usePrevious() call because we want to
// limit actions to once per page, not once per component.
const memory = new Map<string, string>() // <id, path_previously_run_on>

export function useInViewEffectOncePerPage({ id, effect }: { id: string; effect: () => void }) {
  const ref = useRef<any>(null)
  const [inView] = useInViewport(ref)
  const pathname = useWindowLocationPathname()

  // When navigating, free up the effect.
  useEffect(() => {
    const previousPathname = memory.get(id)

    if (pathname !== previousPathname) {
      memory.delete(id)
    }
  }, [id, pathname])

  const stableEffect = useEventCallback(effect)

  // Run the effect once per page when the element comes into view.
  useEffect(() => {
    const previousPathname = memory.get(id)

    if (pathname !== previousPathname && inView) {
      stableEffect()
      memory.set(id, pathname)
    }
  }, [id, inView, stableEffect, pathname])

  return ref
}
